import { StorageAuth } from "./../localStorage/StorageAuth";
import { SWRResponse } from "swr";
import {
  ApiSchemaFinanceCaregiver,
  ApiSchemaFinancePatientDetails,
} from "../../models/api";
import { AxiosAuth } from "../axios";
import {
  useExtendedApiAccess,
  CreateAccessObject,
} from "./useExtendedApiAccess";
import { AppRouteApi } from "../../config/appRoute";
import { PaginatedData } from "../../models/api";
import { useRef, useEffect } from "react";
import { ApiResponseContainer } from "../../models/api";
import { useAppDispatch } from "../redux/ReduxStore";
import { ActionsCaregiverTable } from "../redux/slices/sliceCaregiverTable";
import {
  showErrorToast,
  showSuccessToast,
} from "../../components/toast.component";
import { toaster } from "rsuite";
import { showLoadingToast } from "../../components/toast.component";
import { IPaymentInfoForm } from "../../models/forms";

const CacheIds = {
  Patient: AppRouteApi.Finance.Patient(),
  PatientDetails: AppRouteApi.Finance.PatientDetails(),
  AllPatients: AppRouteApi.Finance.AllPatients(),
  Caregiver: AppRouteApi.Finance.Caregiver(),
  AllCaregivers: AppRouteApi.Finance.AllCaregivers(),
};

function useApiFinancePatient(
  companyId?: string
): SWRResponse<ApiSchemaFinancePatientDetails[] | undefined, Error> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaFinancePatientDetails[]>(
    CacheIds.PatientDetails,
    () => getFinances(companyId || StorageAuth.CompanyId!)
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCaregiverTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaFinancePatientDetails[]>
        >(CacheIds.AllPatients, {
          params: {
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsCaregiverTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return api;
}

function useApiFinanceCaregiver(
  companyId?: string
): SWRResponse<ApiSchemaFinanceCaregiver[] | undefined, Error> {
  const dispatch = useAppDispatch();
  const isMounted = useRef(false);

  const api = useExtendedApiAccess<ApiSchemaFinanceCaregiver[]>(
    CacheIds.Caregiver,
    () =>
      AxiosAuth.get(CacheIds.AllCaregivers, {
        params: {
          CompanyId: companyId ? companyId : StorageAuth.CompanyId,
        },
      })
  );

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    } else {
      (async () => {
        dispatch(ActionsCaregiverTable.setIsLoading(true));
        const res = await AxiosAuth.get<
          ApiResponseContainer<ApiSchemaFinanceCaregiver[]>
        >(CacheIds.AllCaregivers, {
          params: {
            CompanyId: companyId ? companyId : StorageAuth.CompanyId,
          },
        });

        if (res.data.isSuccess) {
          api.mutate(res.data.data, false);
        }
        dispatch(ActionsCaregiverTable.setIsLoading(false));
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  return api;
}

const AccessObject = {
  Caregiver: CreateAccessObject<
    PaginatedData<ApiSchemaFinanceCaregiver>,
    typeof useApiFinanceCaregiver
  >(CacheIds.Caregiver, useApiFinanceCaregiver),

  Patient: CreateAccessObject<
    PaginatedData<ApiSchemaFinancePatientDetails>,
    typeof useApiFinancePatient
  >(CacheIds.PatientDetails, useApiFinancePatient),
};

async function MarkAsPaidPatient(
  data: IPaymentInfoForm
): Promise<ApiResponseContainer<boolean>> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Finance.MarkAsPaidPatient(),
      data
    );
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function MarkAsPaidCaregiver(
  data: IPaymentInfoForm
): Promise<ApiResponseContainer<boolean>> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Finance.MarkAsPaidCaregiver(),
      data
    );
    return response.data;
  } catch (e: any) {
    return showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
  }
}

async function EmailCaseManager(
  sKeys: string[]
): Promise<ApiResponseContainer<boolean>> {
  let toastId = "";
  try {
    toastId = showLoadingToast("Sending Email...", 150000);
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Finance.EmailCaseManager(),
      sKeys
    );
    showSuccessToast("Email Sent");
    toaster.remove(toastId);
    return response.data;
  } catch (e: any) {
    toaster.remove(toastId);
    setTimeout(() => {
      showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
    }, 500);
    return { isSuccess: false };
  }
}

async function SendEmail(
  emailBody: any
): Promise<ApiResponseContainer<boolean>> {
  let toastId = "";
  try {
    toastId = showLoadingToast("Sending Email...", 150000);
    const response = await AxiosAuth.post<ApiResponseContainer<boolean>>(
      AppRouteApi.Finance.SendMail(),
      emailBody
    );
    showSuccessToast("Email Sent");
    toaster.remove(toastId);
    return response.data;
  } catch (e: any) {
    toaster.remove(toastId);
    setTimeout(() => {
      showErrorToast({ isSuccess: false, ...JSON.parse(e.message).data });
    }, 500);
    return { isSuccess: false };
  }
}

async function GenerateInvoice() {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<any>>(
      AppRouteApi.Finance.GenerateInvoice()
    );
    if (response.data?.isSuccess) {
      showSuccessToast("Invoice generated successfully");
    }
    return response.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
    console.debug("Error: ", error);
    return { isSuccess: false };
  }
}

async function GenerateInvoiceV2(invokeType?: string, selectedDate?: Date) {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<any>>(
      AppRouteApi.Finance.GenerateInvoiceV2(invokeType, selectedDate)
    );
    if (response.data?.isSuccess) {
      showSuccessToast("Invoice generated successfully");
    }
    return response.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
    console.debug("Error: ", error);
    return { isSuccess: false };
  }
}

async function GenerateInvoiceV5(invokeType?: string, selectedDate?: Date) {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<any>>(
      AppRouteApi.Finance.GenerateInvoiceV5(invokeType, selectedDate)
    );
    if (response.data?.isSuccess) {
      showSuccessToast("Invoice generated successfully");
    }
    return response.data;
  } catch (error: any) {
    showErrorToast({ isSuccess: false, ...JSON.parse(error.message).data });
    console.debug("Error: ", error);
    return { isSuccess: false };
  }
}

async function InvoiceByCaregiverSkey(
  caregiverSkey: string,
  selectedDate: Date,
  invokeType: string
): Promise<ApiResponseContainer<any>> {
  try {
    const response = await AxiosAuth.post<ApiResponseContainer<any>>(
      AppRouteApi.Finance.InvoiceByCaregiverSkey(
        caregiverSkey,
        selectedDate,
        invokeType
      )
    );
    return response.data;
  } catch (error: any) {
    showErrorToast({
      isSuccess: false,
      message:
        'Invoice already generated for this timeframe, ' +
        (JSON.parse(error.message).data?.message || 'Please contact support.'),
      ...JSON.parse(error.message).data
    });
    console.debug("Error: ", error);
    return { isSuccess: false };
  }
}

export const ApiFinance = {
  Patient: AccessObject.Patient,
  Caregiver: AccessObject.Caregiver,
  MarkAsPaidCaregiver,
  MarkAsPaidPatient,
  EmailCaseManager,
  SendEmail,
  GenerateInvoice,
  GenerateInvoiceV2,
  GenerateInvoiceV5,
  InvoiceByCaregiverSkey,
};

export function getFinances(companyId: string) {
  return AxiosAuth.get<ApiResponseContainer<ApiSchemaFinancePatientDetails[]>>(
    CacheIds.AllPatients,
    {
      params: {
        CompanyId: companyId,
      },
    }
  );
}

export const FinanceCacheIds = CacheIds;
